"use client";

import { c as _c } from "react/compiler-runtime";
import { useCompactMode } from "@/contexts/CompactModeContext";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { ApiUpdateDropRequest } from "@/generated/models/ApiUpdateDropRequest";
import { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropUpdateMutation } from "@/hooks/drops/useDropUpdateMutation";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import { selectEditingDropId, setEditingDropId } from "@/store/editSlice";
import { ActiveDropState } from "@/types/dropInteractionTypes";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DropInteractionParams, DropLocation } from "./Drop";
import WaveDropActions from "./WaveDropActions";
import WaveDropAuthorPfp from "./WaveDropAuthorPfp";
import WaveDropContent from "./WaveDropContent";
import WaveDropHeader from "./WaveDropHeader";
import WaveDropMetadata from "./WaveDropMetadata";
import WaveDropMobileMenu from "./WaveDropMobileMenu";
import WaveDropRatings from "./WaveDropRatings";
import WaveDropReactions from "./WaveDropReactions";
import WaveDropReply from "./WaveDropReply";
enum GroupingThreshold {
  TIME_DIFFERENCE = 60000,
}
const shouldGroupWithDrop = (currentDrop: ExtendedDrop, otherDrop: ExtendedDrop | null): boolean => {
  if (!otherDrop || currentDrop.parts.length > 1) {
    return false;
  }
  const isSameAuthor = currentDrop.author.handle === otherDrop.author.handle;
  const isWithinTimeThreshold = Math.abs(currentDrop.created_at - otherDrop.created_at) <= GroupingThreshold.TIME_DIFFERENCE;
  if (!isSameAuthor || !isWithinTimeThreshold) {
    return false;
  }
  const bothNotReplies = !currentDrop.reply_to && !otherDrop.reply_to;
  const repliesInSameThread = currentDrop.reply_to?.drop_id === otherDrop.reply_to?.drop_id;
  return bothNotReplies || repliesInSameThread;
};
const RANK_STYLES = {
  1: "tw-border-l tw-border-[#E8D48A]/40 tw-border-y-0 tw-border-r-0",
  2: "tw-border-l tw-border-[#DDDDDD]/40 tw-border-y-0 tw-border-r-0",
  3: "tw-border-l tw-border-[#CD7F32]/40 tw-border-y-0 tw-border-r-0",
  default: "tw-border-l tw-border-iron-600/40 tw-border-y-0 tw-border-r-0"
} as const;
const getColorClasses = ({
  isActiveDrop,
  rank,
  isDrop,
  location
}: {
  isActiveDrop: boolean;
  rank: number | null;
  isDrop: boolean;
  location: DropLocation;
}): string => {
  if (isActiveDrop) {
    return "tw-bg-[#3CCB7F]/10 tw-border-l tw-border-l-[#3CCB7F] tw-border-solid tw-border-y-0 tw-border-r-0";
  }
  if (!isDrop) {
    const isWaveView = location === DropLocation.WAVE;
    const hoverClass = isWaveView ? "desktop-hover:hover:tw-bg-iron-800/50" : "";
    const ringClasses = !isWaveView ? "tw-ring-1 tw-ring-inset tw-ring-iron-800" : "";
    const bgClass = !isWaveView ? "tw-bg-iron-950" : "";
    return `${bgClass} ${ringClasses} ${hoverClass}`.trim();
  }
  const rankClass = RANK_STYLES[rank as keyof typeof RANK_STYLES] ?? RANK_STYLES.default;
  return `${rankClass} tw-transition-shadow tw-duration-300`.trim();
};
const getDropClasses = (isActiveDrop: boolean, groupingClass: string, location: DropLocation, rank: number | null, isDrop: boolean): string => {
  const baseClasses = "touch-select-none tw-cursor-default tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-px-4 tw-transition-colors tw-duration-300";
  const streamClasses = "tw-rounded-xl";
  const chatDropClasses = isDrop ? "tw-rounded-lg tw-my-0.5" : "";
  const rankClasses = getColorClasses({
    isActiveDrop,
    rank,
    isDrop,
    location
  });
  const locationClasses = location === DropLocation.MY_STREAM || location === DropLocation.PROFILE ? streamClasses : chatDropClasses;
  return `${baseClasses} ${groupingClass} ${locationClasses} ${rankClasses}`.trim();
};
interface WaveDropProps {
  readonly drop: ExtendedDrop;
  readonly previousDrop: ExtendedDrop | null;
  readonly nextDrop: ExtendedDrop | null;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly dropViewDropId: string | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuote: (param: DropInteractionParams) => void;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: (drop: ExtendedDrop) => void;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null>;
}
const WaveDrop = t0 => {
  const $ = _c(138);
  const {
    drop,
    previousDrop,
    nextDrop,
    showWaveInfo,
    activeDrop,
    location,
    dropViewDropId,
    onReply,
    onQuote,
    onReplyClick,
    onQuoteClick,
    onDropContentClick,
    showReplyAndQuote,
    parentContainerRef
  } = t0;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const dispatch = useDispatch();
  const editingDropId = useSelector(selectEditingDropId);
  const isEditing = editingDropId === drop.id;
  const longPressTimeoutRef = useRef(null);
  const touchStartPosition = useRef(null);
  const dropUpdateMutation = useDropUpdateMutation();
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const isStorm = drop.parts.length > 1;
  const isDrop = drop.drop_type === ApiDropType.Participatory;
  let t1;
  if ($[0] !== drop || $[1] !== isDrop || $[2] !== previousDrop) {
    t1 = !isDrop && shouldGroupWithDrop(drop, previousDrop);
    $[0] = drop;
    $[1] = isDrop;
    $[2] = previousDrop;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const shouldGroupWithPreviousDrop = t1;
  let t2;
  if ($[4] !== drop || $[5] !== isDrop || $[6] !== nextDrop) {
    t2 = !isDrop && shouldGroupWithDrop(drop, nextDrop);
    $[4] = drop;
    $[5] = isDrop;
    $[6] = nextDrop;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const shouldGroupWithNextDrop = t2;
  const isMobile = useIsMobileDevice();
  const compact = useCompactMode();
  const isProfileView = location === DropLocation.PROFILE;
  const showAuthorInfo = !shouldGroupWithPreviousDrop || isProfileView;
  let t3;
  if ($[8] !== isProfileView || $[9] !== shouldGroupWithNextDrop || $[10] !== shouldGroupWithPreviousDrop) {
    t3 = () => {
      if (isProfileView) {
        return "tw-py-4";
      }
      if (shouldGroupWithPreviousDrop) {
        return "tw-pt-1";
      }
      if (shouldGroupWithNextDrop) {
        return "tw-pt-4 tw-pb-1";
      }
      return "tw-py-4";
    };
    $[8] = isProfileView;
    $[9] = shouldGroupWithNextDrop;
    $[10] = shouldGroupWithPreviousDrop;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const getGroupingClass = t3;
  const groupingClass = getGroupingClass();
  let t4;
  if ($[12] !== dispatch || $[13] !== editingDropId || $[14] !== isMobile) {
    t4 = () => {
      if (!isMobile) {
        return;
      }
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[12] = dispatch;
    $[13] = editingDropId;
    $[14] = isMobile;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleLongPress = t4;
  let t5;
  if ($[16] !== handleLongPress || $[17] !== isEditing || $[18] !== isMobile) {
    t5 = e => {
      if (!isMobile) {
        return;
      }
      if (isEditing) {
        return;
      }
      const touch = e.touches[0];
      touchStartPosition.current = {
        x: touch.clientX,
        y: touch.clientY
      };
      longPressTimeoutRef.current = setTimeout(handleLongPress, 500);
    };
    $[16] = handleLongPress;
    $[17] = isEditing;
    $[18] = isMobile;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const handleTouchStart = t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_0 => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
      touchStartPosition.current = null;
    };
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const handleTouchEnd = t6;
  let t7;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_1 => {
      if (!touchStartPosition.current) {
        return;
      }
      const touch_0 = e_1.touches[0];
      const deltaX = Math.abs(touch_0.clientX - touchStartPosition.current.x);
      const deltaY = Math.abs(touch_0.clientY - touchStartPosition.current.y);
      if (deltaX > 10 || deltaY > 10) {
        if (longPressTimeoutRef.current) {
          clearTimeout(longPressTimeoutRef.current);
        }
      }
    };
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const handleTouchMove = t7;
  let t8;
  if ($[22] !== activePartIndex || $[23] !== dispatch || $[24] !== drop || $[25] !== editingDropId || $[26] !== onReply) {
    t8 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[22] = activePartIndex;
    $[23] = dispatch;
    $[24] = drop;
    $[25] = editingDropId;
    $[26] = onReply;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const handleOnReply = t8;
  let t9;
  if ($[28] !== activePartIndex || $[29] !== dispatch || $[30] !== drop || $[31] !== editingDropId || $[32] !== onQuote) {
    t9 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
      onQuote({
        drop,
        partId: drop.parts[activePartIndex].part_id
      });
    };
    $[28] = activePartIndex;
    $[29] = dispatch;
    $[30] = drop;
    $[31] = editingDropId;
    $[32] = onQuote;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleOnQuote = t9;
  let t10;
  if ($[34] !== dispatch || $[35] !== editingDropId) {
    t10 = () => {
      if (editingDropId) {
        dispatch(setEditingDropId(null));
      }
      setIsSlideUp(false);
    };
    $[34] = dispatch;
    $[35] = editingDropId;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  const handleOnAddReaction = t10;
  let t11;
  if ($[37] !== dispatch || $[38] !== drop.id) {
    t11 = () => {
      setIsSlideUp(false);
      dispatch(setEditingDropId(drop.id));
    };
    $[37] = dispatch;
    $[38] = drop.id;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  const handleOnEdit = t11;
  let t12;
  if ($[40] !== activePartIndex || $[41] !== dispatch || $[42] !== drop || $[43] !== dropUpdateMutation) {
    t12 = async (newContent, mentions) => {
      const cleanedMentions = (mentions || drop.mentioned_users).map(_temp);
      const updateRequest = {
        parts: drop.parts.map((part, index) => ({
          content: index === activePartIndex ? newContent : part.content,
          quoted_drop: part.quoted_drop || null,
          media: part.media || []
        })),
        title: drop.title,
        metadata: drop.metadata,
        referenced_nfts: drop.referenced_nfts,
        mentioned_users: cleanedMentions,
        signature: null
      };
      dispatch(setEditingDropId(null));
      dropUpdateMutation.mutate({
        dropId: drop.id,
        request: updateRequest,
        currentDrop: drop
      });
    };
    $[40] = activePartIndex;
    $[41] = dispatch;
    $[42] = drop;
    $[43] = dropUpdateMutation;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  const handleEditSave = t12;
  let t13;
  if ($[45] !== dispatch) {
    t13 = () => {
      dispatch(setEditingDropId(null));
    };
    $[45] = dispatch;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const handleEditCancel = t13;
  let t14;
  let t15;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => () => {
      if (longPressTimeoutRef.current) {
        clearTimeout(longPressTimeoutRef.current);
      }
    };
    t15 = [];
    $[47] = t14;
    $[48] = t15;
  } else {
    t14 = $[47];
    t15 = $[48];
  }
  useEffect(t14, t15);
  let t16;
  if ($[49] !== isEditing || $[50] !== isSlideUp) {
    t16 = () => {
      if (isEditing && isSlideUp) {
        setIsSlideUp(false);
      }
    };
    $[49] = isEditing;
    $[50] = isSlideUp;
    $[51] = t16;
  } else {
    t16 = $[51];
  }
  let t17;
  if ($[52] !== isEditing) {
    t17 = [isEditing];
    $[52] = isEditing;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  useEffect(t16, t17);
  const dropClasses = getDropClasses(isActiveDrop, groupingClass, location, drop.rank, isDrop);
  const t18 = `${isDrop && location === DropLocation.WAVE ? "tw-py-0.5 tw-px-4" : ""} ${isProfileView ? "tw-mb-3" : ""} tw-w-full`;
  const t19 = drop.stableHash ?? drop.id;
  let t20;
  if ($[54] !== drop.author || $[55] !== drop.reply_to || $[56] !== drop.wave || $[57] !== dropViewDropId || $[58] !== onReplyClick || $[59] !== previousDrop?.author || $[60] !== previousDrop?.reply_to?.drop_id) {
    t20 = drop.reply_to && (drop.reply_to.drop_id !== previousDrop?.reply_to?.drop_id || drop.author.handle !== previousDrop?.author.handle) && drop.reply_to.drop_id !== dropViewDropId && <WaveDropReply onReplyClick={onReplyClick} dropId={drop.reply_to.drop_id} dropPartId={drop.reply_to.drop_part_id} maybeDrop={drop.reply_to.drop ? {
      ...drop.reply_to.drop,
      wave: drop.wave
    } : null} />;
    $[54] = drop.author;
    $[55] = drop.reply_to;
    $[56] = drop.wave;
    $[57] = dropViewDropId;
    $[58] = onReplyClick;
    $[59] = previousDrop?.author;
    $[60] = previousDrop?.reply_to?.drop_id;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== drop || $[63] !== showAuthorInfo) {
    t21 = showAuthorInfo && <WaveDropAuthorPfp drop={drop} />;
    $[62] = drop;
    $[63] = showAuthorInfo;
    $[64] = t21;
  } else {
    t21 = $[64];
  }
  const t22 = showAuthorInfo ? "calc(100% - 3.5rem)" : "100%";
  let t23;
  if ($[65] !== t22) {
    t23 = {
      maxWidth: t22
    };
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== activePartIndex || $[68] !== drop || $[69] !== isStorm || $[70] !== showAuthorInfo || $[71] !== showWaveInfo) {
    t24 = showAuthorInfo && <WaveDropHeader drop={drop} showWaveInfo={showWaveInfo} isStorm={isStorm} currentPartIndex={activePartIndex} partsCount={drop.parts.length} />;
    $[67] = activePartIndex;
    $[68] = drop;
    $[69] = isStorm;
    $[70] = showAuthorInfo;
    $[71] = showWaveInfo;
    $[72] = t24;
  } else {
    t24 = $[72];
  }
  const t25 = shouldGroupWithPreviousDrop && !isProfileView ? "tw-ml-[3.25rem]" : "";
  let t26;
  if ($[73] !== activePartIndex || $[74] !== drop || $[75] !== dropUpdateMutation.isPending || $[76] !== handleEditCancel || $[77] !== handleEditSave || $[78] !== handleLongPress || $[79] !== isEditing || $[80] !== onDropContentClick || $[81] !== onQuoteClick || $[82] !== parentContainerRef) {
    t26 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} parentContainerRef={parentContainerRef} isEditing={isEditing} isSaving={dropUpdateMutation.isPending} onSave={handleEditSave} onCancel={handleEditCancel} />;
    $[73] = activePartIndex;
    $[74] = drop;
    $[75] = dropUpdateMutation.isPending;
    $[76] = handleEditCancel;
    $[77] = handleEditSave;
    $[78] = handleLongPress;
    $[79] = isEditing;
    $[80] = onDropContentClick;
    $[81] = onQuoteClick;
    $[82] = parentContainerRef;
    $[83] = t26;
  } else {
    t26 = $[83];
  }
  let t27;
  if ($[84] !== t25 || $[85] !== t26) {
    t27 = <div className={t25}>{t26}</div>;
    $[84] = t25;
    $[85] = t26;
    $[86] = t27;
  } else {
    t27 = $[86];
  }
  let t28;
  if ($[87] !== t23 || $[88] !== t24 || $[89] !== t27) {
    t28 = <div className="tw-flex tw-flex-col tw-w-full tw-gap-y-1" style={t23}>{t24}{t27}</div>;
    $[87] = t23;
    $[88] = t24;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  let t29;
  if ($[91] !== t21 || $[92] !== t28) {
    t29 = <div className="tw-flex tw-gap-x-3 tw-relative tw-z-10 tw-w-full tw-text-left tw-bg-transparent tw-border-0">{t21}{t28}</div>;
    $[91] = t21;
    $[92] = t28;
    $[93] = t29;
  } else {
    t29 = $[93];
  }
  let t30;
  if ($[94] !== activePartIndex || $[95] !== drop || $[96] !== handleOnEdit || $[97] !== handleOnQuote || $[98] !== handleOnReply || $[99] !== isEditing || $[100] !== isMobile || $[101] !== showReplyAndQuote) {
    t30 = !isMobile && showReplyAndQuote && !isEditing && <WaveDropActions drop={drop} activePartIndex={activePartIndex} onReply={handleOnReply} onQuote={handleOnQuote} onEdit={handleOnEdit} />;
    $[94] = activePartIndex;
    $[95] = drop;
    $[96] = handleOnEdit;
    $[97] = handleOnQuote;
    $[98] = handleOnReply;
    $[99] = isEditing;
    $[100] = isMobile;
    $[101] = showReplyAndQuote;
    $[102] = t30;
  } else {
    t30 = $[102];
  }
  const t31 = `tw-mx-2 tw-flex tw-items-center tw-gap-x-2 tw-gap-y-1 tw-flex-wrap ${compact ? "tw-ml-[2.75rem] tw-w-[calc(100%-2.5rem)]" : "tw-ml-[3.25rem] tw-w-[calc(100%-3.25rem)]"}`;
  let t32;
  if ($[103] !== drop.metadata) {
    t32 = drop.metadata.length > 0 && <WaveDropMetadata metadata={drop.metadata} />;
    $[103] = drop.metadata;
    $[104] = t32;
  } else {
    t32 = $[104];
  }
  let t33;
  if ($[105] !== drop) {
    t33 = !!drop.raters_count && <WaveDropRatings drop={drop} />;
    $[105] = drop;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  let t34;
  if ($[107] !== drop) {
    t34 = <WaveDropReactions drop={drop} />;
    $[107] = drop;
    $[108] = t34;
  } else {
    t34 = $[108];
  }
  let t35;
  if ($[109] !== t31 || $[110] !== t32 || $[111] !== t33 || $[112] !== t34) {
    t35 = <div className={t31}>{t32}{t33}{t34}</div>;
    $[109] = t31;
    $[110] = t32;
    $[111] = t33;
    $[112] = t34;
    $[113] = t35;
  } else {
    t35 = $[113];
  }
  let t36;
  if ($[114] !== drop || $[115] !== handleOnAddReaction || $[116] !== handleOnEdit || $[117] !== handleOnQuote || $[118] !== handleOnReply || $[119] !== isSlideUp || $[120] !== longPressTriggered || $[121] !== showReplyAndQuote) {
    t36 = <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onQuote={handleOnQuote} onAddReaction={handleOnAddReaction} onEdit={handleOnEdit} />;
    $[114] = drop;
    $[115] = handleOnAddReaction;
    $[116] = handleOnEdit;
    $[117] = handleOnQuote;
    $[118] = handleOnReply;
    $[119] = isSlideUp;
    $[120] = longPressTriggered;
    $[121] = showReplyAndQuote;
    $[122] = t36;
  } else {
    t36 = $[122];
  }
  let t37;
  if ($[123] !== drop.serial_no || $[124] !== dropClasses || $[125] !== handleTouchEnd || $[126] !== handleTouchMove || $[127] !== handleTouchStart || $[128] !== t19 || $[129] !== t20 || $[130] !== t29 || $[131] !== t30 || $[132] !== t35 || $[133] !== t36) {
    t37 = <div className={dropClasses} data-wave-drop-id={t19} data-serial-no={drop.serial_no} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} onTouchMove={handleTouchMove}>{t20}{t29}{t30}{t35}{t36}</div>;
    $[123] = drop.serial_no;
    $[124] = dropClasses;
    $[125] = handleTouchEnd;
    $[126] = handleTouchMove;
    $[127] = handleTouchStart;
    $[128] = t19;
    $[129] = t20;
    $[130] = t29;
    $[131] = t30;
    $[132] = t35;
    $[133] = t36;
    $[134] = t37;
  } else {
    t37 = $[134];
  }
  let t38;
  if ($[135] !== t18 || $[136] !== t37) {
    t38 = <div className={t18}>{t37}</div>;
    $[135] = t18;
    $[136] = t37;
    $[137] = t38;
  } else {
    t38 = $[137];
  }
  return t38;
};
export default memo(WaveDrop);
function _temp(user) {
  return {
    mentioned_profile_id: user.mentioned_profile_id,
    handle_in_content: user.handle_in_content
  };
}